import Decimal from 'decimal.js';
import { compact } from 'lodash';

import { FUNCTIONAL } from '@/styles/tokens/colors';
import { formatCurrencyNoDecimals } from '@/utils/formatting/currency';

import { EstateWaterfall_TransferLogItemFragment } from '../../../graphql/EstateWaterfall.generated';
import { EdgeLabelCard } from '../edgeLabelCard.component';
import { CardConfig } from './types';

interface CltFundingCardParams {
  sourceEntityName: string;
  targetEntityName: string;
  transfer?: EstateWaterfall_TransferLogItemFragment;
}

export function createCltFundingCard({
  sourceEntityName,
  targetEntityName,
  transfer,
}: CltFundingCardParams): CardConfig {
  const grantor = sourceEntityName;
  const cltName = targetEntityName;

  const isHypothetical = transfer?.isHypothetical;

  // Get transfer amount
  const fundingAmount = transfer?.finalTransferAmount || new Decimal(0);

  const term = (() => {
    if (transfer?.associatedObject?.__typename === 'HypotheticalCLT') {
      const numYears =
        transfer.associatedObject.simulatedClt.cltProposal?.termYears;
      return numYears ? `${numYears} years` : null;
    }

    if (
      transfer?.associatedObject?.__typename === 'Entity' &&
      transfer.associatedObject.subtype.__typename === 'CLTTrust'
    ) {
      const duration = transfer.associatedObject.subtype.termDurationYears;
      return duration ? `${duration} years` : null;
    }

    return null;
  })();

  const termEndYear = (() => {
    if (transfer?.associatedObject?.__typename === 'HypotheticalCLT') {
      const numYears =
        transfer.associatedObject.simulatedClt.cltProposal?.termYears;
      return numYears ? new Date().getFullYear() + numYears : null;
    }

    if (
      transfer?.associatedObject?.__typename === 'Entity' &&
      transfer.associatedObject.subtype.__typename === 'CLTTrust'
    ) {
      return transfer.associatedObject.subtype.termEndDate?.getFullYear();
    }

    return null;
  })();

  return {
    element: (
      <EdgeLabelCard
        key={`clt-funding-${sourceEntityName}-${targetEntityName}`}
        title={isHypothetical ? 'Hypothetical CLT funding' : 'CLT funding'}
        titleSx={
          isHypothetical ? { color: FUNCTIONAL.WARNING[600] } : undefined
        }
        description={`${grantor} transfers assets to ${cltName}.`}
        rows={compact([
          {
            label: 'Funding amount',
            value: formatCurrencyNoDecimals(fundingAmount),
          },
          {
            label: 'CLT term',
            value: term,
          },
          termEndYear
            ? {
                label: 'Term end',
                value: termEndYear.toString(),
              }
            : null,
        ])}
      />
    ),
    label: 'CLT funding',
    value: formatCurrencyNoDecimals(fundingAmount),
    variant: isHypothetical ? 'secondary' : 'primary',
  };
}
